/**
 * Mermaid Tool - API 服務層
 * static/js/mermaid/api.js
 */

/**
 * API 錯誤類別
 */
export class ApiError extends Error {
    constructor(message, status, data = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

/**
 * 發送 API 請求的通用函數
 * @param {string} url - API 端點
 * @param {Object} options - fetch 選項
 * @returns {Promise<Object>} 回應資料
 */
async function request(url, options = {}) {
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json'
        }
    };
    
    const mergedOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers
        }
    };
    
    try {
        const response = await fetch(url, mergedOptions);
        const data = await response.json();
        
        if (!response.ok || data.success === false) {
            throw new ApiError(
                data.error || data.message || '請求失敗',
                response.status,
                data
            );
        }
        
        return data;
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(error.message || '網路錯誤', 0);
    }
}

// ============== 圖表 API ==============

/**
 * 儲存新圖表
 * @param {Object} diagram - 圖表資料
 * @param {string} diagram.title - 標題
 * @param {string} diagram.diagram_code - Mermaid 程式碼
 * @param {string} diagram.diagram_type - 圖表類型
 * @returns {Promise<Object>} 儲存結果
 */
export async function saveDiagram({ title, diagram_code, diagram_type }) {
    return request('/api/mermaid/save', {
        method: 'POST',
        body: JSON.stringify({ title, diagram_code, diagram_type })
    });
}

/**
 * 更新圖表
 * @param {number} diagramId - 圖表 ID
 * @param {Object} updates - 更新內容
 * @returns {Promise<Object>} 更新結果
 */
export async function updateDiagram(diagramId, updates) {
    return request(`/api/mermaid/${diagramId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
    });
}

/**
 * 獲取圖表列表
 * @param {number} [limit] - 限制數量
 * @returns {Promise<Object>} 圖表列表
 */
export async function listDiagrams(limit = null) {
    const url = limit ? `/api/mermaid/list?limit=${limit}` : '/api/mermaid/list';
    return request(url);
}

/**
 * 獲取單一圖表
 * @param {number} diagramId - 圖表 ID
 * @returns {Promise<Object>} 圖表資料
 */
export async function getDiagram(diagramId) {
    return request(`/api/mermaid/${diagramId}`);
}

/**
 * 刪除圖表
 * @param {number} diagramId - 圖表 ID
 * @returns {Promise<Object>} 刪除結果
 */
export async function deleteDiagram(diagramId) {
    return request(`/api/mermaid/${diagramId}`, {
        method: 'DELETE'
    });
}

// ============== 分享 API ==============

/**
 * 建立分享連結
 * @param {number} diagramId - 圖表 ID
 * @param {Object} options - 分享選項
 * @param {string} options.share_type - 分享類型 ('public' 或 'password')
 * @param {string} [options.password] - 密碼（密碼保護用）
 * @param {string} [options.expires_at] - 過期時間
 * @returns {Promise<Object>} 分享資料
 */
export async function createShare(diagramId, options = {}) {
    return request(`/api/mermaid/${diagramId}/share`, {
        method: 'POST',
        body: JSON.stringify({
            share_type: options.share_type || 'public',
            password: options.password,
            expires_at: options.expires_at
        })
    });
}

/**
 * 獲取圖表的分享列表
 * @param {number} diagramId - 圖表 ID
 * @returns {Promise<Object>} 分享列表
 */
export async function listShares(diagramId) {
    return request(`/api/mermaid/${diagramId}/shares`);
}

/**
 * 刪除分享連結
 * @param {number} shareId - 分享 ID
 * @returns {Promise<Object>} 刪除結果
 */
export async function deleteShare(shareId) {
    return request(`/api/mermaid/share/${shareId}`, {
        method: 'DELETE'
    });
}

/**
 * 驗證分享密碼
 * @param {string} shareToken - 分享 token
 * @param {string} password - 密碼
 * @returns {Promise<Object>} 驗證結果與圖表資料
 */
export async function verifySharePassword(shareToken, password) {
    return request(`/api/mermaid/share/${shareToken}/verify`, {
        method: 'POST',
        body: JSON.stringify({ password })
    });
}
